import { getAgentUrls } from "./agentEndpoint";
import { authHeaders } from "./auth";

/**
 * Speech-to-text for the "Scribe all SOAP" flow.
 *
 *  - AudioRecorder: captures microphone audio in the browser (MediaRecorder).
 *  - transcribeWithCariva: uploads the recording to the gateway's server-side
 *    CARIVA proxy (POST <agent>/transcribe). The CARIVA key + secret live ONLY
 *    on the server; the browser never sees them. The gateway returns the raw
 *    transcript plus the four structured SOAP sections (doc-ipd-soap).
 *
 * Best-effort: if the proxy is unreachable / CARIVA is not configured / nothing
 * usable comes back, returns null and the caller falls back to simulation.
 * Doctor Orders are NOT produced by CARIVA — the caller parses those from the
 * transcript and CARIVA's Plan section.
 */

export function isRecordingSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window.MediaRecorder !== "undefined"
  );
}

function pickMimeType(): string {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const type of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) return type;
  }
  return "";
}

/** Records microphone audio until stop() is called, returning the audio Blob. */
export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private stream: MediaStream | null = null;

  async start(): Promise<boolean> {
    if (!isRecordingSupported()) return false;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
      this.chunks = [];
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.chunks.push(event.data);
      };
      this.mediaRecorder.start();
      return true;
    } catch {
      this.cleanup();
      return false;
    }
  }

  stop(): Promise<Blob | null> {
    return new Promise((resolve) => {
      const recorder = this.mediaRecorder;
      if (!recorder) {
        resolve(null);
        return;
      }
      recorder.onstop = () => {
        const blob = this.chunks.length ? new Blob(this.chunks, { type: recorder.mimeType || "audio/webm" }) : null;
        this.cleanup();
        resolve(blob);
      };
      try {
        recorder.stop();
      } catch {
        this.cleanup();
        resolve(null);
      }
    });
  }

  private cleanup() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.mediaRecorder = null;
    this.chunks = [];
  }
}

export type CarivaSoap = { subjective: string; objective: string; assessment: string; plan: string };
export type CarivaResult = { transcript: string; soap: CarivaSoap };

function transcribeUrl(): string | null {
  const { invokeUrl } = getAgentUrls();
  // The gateway exposes POST <agent>/transcribe. Only meaningful when the SPA
  // talks to the real gateway (agent endpoint), not the same-origin Next proxy.
  if (invokeUrl.endsWith("/agent")) return `${invokeUrl}/transcribe`;
  return null;
}

/**
 * Send a recording to the server-side CARIVA proxy. Returns the transcript plus
 * the four structured SOAP sections, or null if the proxy is unreachable /
 * CARIVA is not configured / nothing usable came back.
 */
export async function transcribeWithCariva(blob: Blob, lang?: string): Promise<CarivaResult | null> {
  const base = transcribeUrl();
  if (!base || !blob) return null;
  const url = lang ? `${base}?lang=${encodeURIComponent(lang)}` : base;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "content-type": blob.type || "audio/webm", ...(await authHeaders()) },
      body: blob,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as {
      transcript?: string | null;
      soap?: Partial<CarivaSoap> | null;
      configured?: boolean;
    };
    if (!data.configured) return null;
    const soap: CarivaSoap = {
      subjective: (data.soap?.subjective ?? "").trim(),
      objective: (data.soap?.objective ?? "").trim(),
      assessment: (data.soap?.assessment ?? "").trim(),
      plan: (data.soap?.plan ?? "").trim(),
    };
    const transcript = (data.transcript ?? "").trim();
    const anySoap = soap.subjective || soap.objective || soap.assessment || soap.plan;
    if (!transcript && !anySoap) return null;
    return { transcript, soap };
  } catch {
    return null;
  }
}
